import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TiInfoOutline } from 'react-icons/ti';
import { ToastContainer, toast } from 'react-toastify'; // Import react-toastify
import 'react-toastify/dist/ReactToastify.css'; // Import toastify styles
import '../styles/widthdrawall.css';

function Widthdrawall() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        amount: '', // Added amount field
        accountType: 'Crypto',
        bankName: '',
        bankAccountNumber: '',
        bankFirstName: '',
        bankLastName: '',
        cryptoWalletAddress: '',
        cryptoType: '',
    });
    const [step, setStep] = useState(1);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false); // Track submission state
    const [username, setUsername] = useState('');

    useEffect(() => {
        try {
            const storedUser = localStorage.getItem("user");
            if (storedUser) {
                const user = JSON.parse(storedUser);
                setUsername(user.username || ''); // Use username from localStorage
            } else {
                setErrors({ general: "No user data found in localStorage." });
            }
        } catch (error) {
            console.error("Error retrieving user data:", error);
            setErrors({ general: "Failed to retrieve user data: " + error.message });
        }
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            delete updatedErrors[name];
            return updatedErrors;
        });
    };

    const handleAccountTypeChange = (e) => {
        setFormData({
            ...formData,
            accountType: e.target.value,
            bankName: '',
            bankAccountNumber: '',
            bankFirstName: '',
            bankLastName: '',
            cryptoWalletAddress: '',
            cryptoType: ''
        });
        setErrors({}); // Clear errors when changing account type
    };

    const handleBack = () => {
        if (step > 1) {
            setStep(step - 1);
            setErrors({});
        } else {
            navigate('/');
        }
    };

    const validateStep1 = () => {
        const newErrors = {};
        if (!formData.email.trim()) newErrors.email = 'this field is required.';
        if (!formData.firstName.trim()) newErrors.firstName = 'this field is required.';
        if (!formData.lastName.trim()) newErrors.lastName = 'this field is required.';
        if (!formData.phone.trim()) newErrors.phone = 'this field is required.';
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep2 = () => {
        const newErrors = {};
        if (formData.accountType === 'Bank') {
            if (!formData.amount || parseFloat(formData.amount) <= 0) newErrors.amount = 'A valid amount greater than 0 is required.';
            if (!formData.bankName.trim()) newErrors.bankName = 'this field is required.';
            if (!formData.bankAccountNumber.trim()) newErrors.bankAccountNumber = 'this field is required.';
            if (!formData.bankFirstName.trim()) newErrors.bankFirstName = 'this field is required.';
            if (!formData.bankLastName.trim()) newErrors.bankLastName = 'this field is required.';
        } else if (formData.accountType === 'Crypto') {
            if (!formData.amount || parseFloat(formData.amount) <= 0) newErrors.amount = 'A valid amount greater than 0 is required.';
            if (!formData.cryptoWalletAddress.trim()) newErrors.cryptoWalletAddress = 'this field is required.';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = async () => {
        if (step === 1) {
            if (validateStep1()) {
                setStep(step + 1);
            }
        } else if (step === 2) {
            if (validateStep2()) {
                if (!username) {
                    setErrors({ submit: "Username is required but not found." });
                    toast.error("Username is required but not found.", {
                        position: "top-right",
                        autoClose: 1000,
                        closeButton: false,
                    });
                    return;
                }
                setIsSubmitting(true); // Disable button during submission
                try {
                    // Map formData to the API payload
                    const payload = {
                        username: username,
                        amount: parseFloat(formData.amount),
                        paymentMethod: formData.accountType.toUpperCase(),
                        firstname: formData.firstName,
                        lastname: formData.lastName,
                        phonenumber: formData.phone,
                        bankname: formData.accountType === 'Bank' ? formData.bankName : null,
                        bankaccount: formData.accountType === 'Bank' ? formData.bankAccountNumber : null,
                        bankfirstname: formData.accountType === 'Bank' ? formData.bankFirstName : null,
                        banklastname: formData.accountType === 'Bank' ? formData.bankLastName : null,
                        cryptoWalletAddress: formData.accountType === 'Crypto' ? formData.cryptoWalletAddress : null
                    };

                    console.log("Submitting data to API:", payload);
                    const baseUrl = process.env.REACT_APP_API_BASE_URL;
                    // POST request to API
                    const response = await fetch(`${baseUrl}/transactions/withdraw`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('token') || ''}` // Include token if required
                        },
                        body: JSON.stringify(payload)
                    });

                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
                    }

                    const result = await response.json();
                    console.log("API response:", result);

                    // On success, reset form and return to step 1
                    setFormData({
                        email: '',
                        firstName: '',
                        lastName: '',
                        phone: '',
                        amount: '',
                        accountType: 'Crypto',
                        bankName: '',
                        bankAccountNumber: '',
                        bankFirstName: '',
                        bankLastName: '',
                        cryptoWalletAddress: '',
                        cryptoType: '',
                    });
                    setStep(1);
                    toast.success("Withdrawal request submitted successfully!", {
                        position: "top-right",
                        autoClose: 1000,
                        closeButton: false,
                    });
                } catch (error) {
                    console.error("Error submitting withdrawal:", error);
                    const errorMessage = error.message || "Failed to submit withdrawal request";
                    setErrors({ submit: errorMessage });
                    // Show toast for "Amount not enough" or other errors
                    toast.error(errorMessage === "Amount not enough" ? "Amount not enough" : "Error: " + errorMessage, {
                        position: "top-right",
                        autoClose: 1000,
                        closeButton: false,
                    });
                } finally {
                    setIsSubmitting(false); // Re-enable button
                }
            }
        }
    };

    return (
        <div className="withdraw-page">
            {/* Add ToastContainer to render toast notifications */}
            <ToastContainer />
            <div className={`withdraw-content ${step === 2 ? 'step-2' : ''}`}>
                <div className="step-indicator">
                    <span className={Object.keys(errors).length > 0 && step === 1 ? 'error-step' : step === 1 ? 'active-step' : 'completed'}>
                        {Object.keys(errors).length > 0 && step === 1 ? <TiInfoOutline className="error-icon" /> : '✔'}
                    </span>
                    <span>Basic Information</span>
                    <span className={Object.keys(errors).length > 0 && step === 2 ? 'error-step' : step === 2 ? 'active-step' : ''}>
                        {Object.keys(errors).length > 0 && step === 2 ? <TiInfoOutline className="error-icon" /> : '2'}
                    </span>
                    <span>Account Information</span>
                </div>

                {step === 1 && (
                    <div className="form-container">
                        <h2>Basic Information</h2>
                        <div className={`form-field ${errors.email ? 'error' : ''}`}>
                            <label>Email Address</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter your email"
                            />
                            {errors.email && <p className="error-message">{errors.email}</p>}
                        </div>
                        <div className={`form-field ${errors.firstName ? 'error' : ''}`}>
                            <label>First Name</label>
                            <input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                placeholder="Enter your first name"
                            />
                            {errors.firstName && <p className="error-message">{errors.firstName}</p>}
                        </div>
                        <div className={`form-field ${errors.lastName ? 'error' : ''}`}>
                            <label>Last Name</label>
                            <input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                placeholder="Enter your last name"
                            />
                            {errors.lastName && <p className="error-message">{errors.lastName}</p>}
                        </div>
                        <div className={`form-field ${errors.phone ? 'error' : ''}`}>
                            <label>Phone Number</label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="Enter your phone number"
                            />
                            {errors.phone && <p className="error-message">{errors.phone}</p>}
                        </div>
                        <div className="form-buttons">
                            <button className="back-button" onClick={handleBack}>BACK</button>
                            <button
                                className={`next-button ${Object.keys(errors).length > 0 ? 'disabled' : ''}`}
                                onClick={handleNext}
                                disabled={Object.keys(errors).length > 0}
                            >
                                NEXT
                            </button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="form-container">
                        <h2>Account Information</h2>
                        <div className="form-field">
                            <label>Account Types</label>
                            <div className="radio-group">
                                <label>
                                    <input
                                        type="radio"
                                        name="accountType"
                                        value="Bank"
                                        checked={formData.accountType === 'Bank'}
                                        onChange={handleAccountTypeChange}
                                    />
                                    Bank
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        name="accountType"
                                        value="Crypto"
                                        checked={formData.accountType === 'Crypto'}
                                        onChange={handleAccountTypeChange}
                                    />
                                    Crypto
                                </label>
                            </div>
                        </div>
                        {formData.accountType === 'Bank' && (
                            <>
                                <div className={`form-field ${errors.amount ? 'error' : ''}`}>
                                    <label>Withdrawal Amount</label>
                                    <input
                                        type="number"
                                        name="amount"
                                        value={formData.amount}
                                        onChange={handleChange}
                                        placeholder="Enter amount"
                                        step="0.01"
                                        min="0.01"
                                    />
                                    {errors.amount && <p className="error-message">{errors.amount}</p>}
                                </div>
                                <div className={`form-field ${errors.bankName ? 'error' : ''}`}>
                                    <label>Bank Name</label>
                                    <input
                                        type="text"
                                        name="bankName"
                                        value={formData.bankName}
                                        onChange={handleChange}
                                        placeholder="Enter bank name"
                                    />
                                    {errors.bankName && <p className="error-message">{errors.bankName}</p>}
                                </div>
                                <div className={`form-field ${errors.bankAccountNumber ? 'error' : ''}`}>
                                    <label>Bank Account Number</label>
                                    <input
                                        type="text"
                                        name="bankAccountNumber"
                                        value={formData.bankAccountNumber}
                                        onChange={handleChange}
                                        placeholder="Enter account number"
                                    />
                                    {errors.bankAccountNumber && <p className="error-message">{errors.bankAccountNumber}</p>}
                                </div>
                                <div className={`form-field ${errors.bankFirstName ? 'error' : ''}`}>
                                    <label>Bank First Name</label>
                                    <input
                                        type="text"
                                        name="bankFirstName"
                                        value={formData.bankFirstName}
                                        onChange={handleChange}
                                        placeholder="Enter first name"
                                    />
                                    {errors.bankFirstName && <p className="error-message">{errors.bankFirstName}</p>}
                                </div>
                                <div className={`form-field ${errors.bankLastName ? 'error' : ''}`}>
                                    <label>Bank Last Name</label>
                                    <input
                                        type="text"
                                        name="bankLastName"
                                        value={formData.bankLastName}
                                        onChange={handleChange}
                                        placeholder="Enter last name"
                                    />
                                    {errors.bankLastName && <p className="error-message">{errors.bankLastName}</p>}
                                </div>
                            </>
                        )}
                        {formData.accountType === 'Crypto' && (
                            <>
                                <div className={`form-field ${errors.amount ? 'error' : ''}`}>
                                    <label>Withdrawal Amount</label>
                                    <input
                                        type="number"
                                        name="amount"
                                        value={formData.amount}
                                        onChange={handleChange}
                                        placeholder="Enter amount"
                                        step="0.01"
                                        min="0.01"
                                    />
                                    {errors.amount && <p className="error-message">{errors.amount}</p>}
                                </div>
                                <div className={`form-field ${errors.cryptoWalletAddress ? 'error' : ''}`}>
                                    <label>Crypto Wallet Address</label>
                                    <input
                                        type="text"
                                        name="cryptoWalletAddress"
                                        value={formData.cryptoWalletAddress}
                                        onChange={handleChange}
                                        placeholder="Enter crypto wallet address"
                                    />
                                    {errors.cryptoWalletAddress && <p className="error-message">{errors.cryptoWalletAddress}</p>}
                                </div>
                            </>
                        )}
                        {errors.submit && <p className="error-message submit-error">{errors.submit}</p>}
                        <div className="form-buttons">
                            <button className="back-button" onClick={handleBack}>BACK</button>
                            <button
                                className={`next-button ${Object.keys(errors).length > 0 || isSubmitting ? 'disabled' : ''}`}
                                onClick={handleNext}
                                disabled={Object.keys(errors).length > 0 || isSubmitting}
                            >
                                {isSubmitting ? 'SUBMITTING...' : 'FINISHED'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Widthdrawall;